import { Users, BookOpen, UserCog, Briefcase, Globe, Calendar, Activity, Monitor, Settings, ShieldCheck, Phone, Megaphone, Handshake, MessageCircle, Presentation } from "lucide-react";

export const permissionCategories = [
  {
    category: "Student Management",
    icon: Users,
    permissions: [
      { key: "view_students", label: "View Students" },
      { key: "add_edit_students", label: "Add / Edit Students" },
      { key: "delete_students", label: "Delete Students" },
      { key: "manage_enrollments", label: "Manage Enrollments" },
    ],
  },
  {
    category: "Course & Batch Management",
    icon: BookOpen,
    permissions: [
      { key: "view_courses", label: "View Courses" },
      { key: "add_edit_courses", label: "Add / Edit Courses" },
      { key: "manage_syllabus", label: "Manage Syllabus" },
      { key: "manage_batches", label: "Manage Batches" },
      { key: "assign_instructors", label: "Assign Instructors" },
    ],
  },
  {
    category: "Instructor Management",
    icon: UserCog,
    permissions: [
      { key: "view_instructors", label: "View Instructors" },
      { key: "add_edit_instructors", label: "Add / Edit Instructors" },
      { key: "manage_schedules", label: "Manage Schedules" },
    ],
  },
  {
    category: "Fee Management",
    icon: Briefcase,
    permissions: [
      { key: "view_fees", label: "View Fees" },
      { key: "collect_payments", label: "Collect Payments" },
      { key: "manage_discounts", label: "Manage Discounts" },
      { key: "generate_invoices", label: "Generate Invoices" },
    ],
  },
  {
    category: "Enquiry Management",
    icon: Globe,
    permissions: [
      { key: "view_enquiries", label: "View Enquiries" },
      { key: "add_edit_enquiries", label: "Add / Edit Enquiries" },
      { key: "convert_to_student", label: "Convert to Student" },
    ],
  },
  {
    category: "Telephony",
    icon: Phone,
    permissions: [
      { key: "view_calls", label: "View Call Logs" },
      { key: "make_calls", label: "Make Calls" },
    ],
  },
  {
    /**
     * Publishing and budget are split out from "manage" on purpose: drafting a
     * campaign is ordinary marketing work, while pushing it live and raising a
     * daily budget spend real money from the institute's own card. A counsellor
     * can be trusted with the first and should not hold the second by default.
     */
    category: "Marketing & Ads",
    icon: Megaphone,
    permissions: [
      { key: "view_campaigns", label: "View Campaigns & Performance" },
      { key: "manage_campaigns", label: "Create / Edit Campaigns" },
      { key: "publish_campaigns", label: "Publish Campaigns (spends money)" },
      { key: "manage_ad_budget", label: "Change Ad Budgets" },
    ],
  },
  {
    category: "WhatsApp",
    icon: MessageCircle,
    permissions: [
      { key: "reply_whatsapp", label: "Reply in WhatsApp Inbox" },
      { key: "manage_whatsapp", label: "Send & Manage WhatsApp" },
    ],
  },
  {
    category: "Demo Classes & Webinars",
    icon: Presentation,
    permissions: [
      { key: "manage_demo_webinars", label: "Create & Manage Demo Classes / Webinars" },
    ],
  },
  {
    category: "Assessments & Interviews",
    icon: Calendar,
    permissions: [
      { key: "manage_interviews", label: "Manage Interviews" },
      { key: "schedule_interviews", label: "Schedule Interviews" },
      { key: "manage_assignments", label: "Manage Assignments" },
      { key: "view_ai_attempts", label: "View AI Attempts" },
      { key: "manage_ai", label: "Manage AI Questions & Settings" },
    ],
  },
  {
    category: "Reports & Analytics",
    icon: Activity,
    permissions: [
      { key: "view_reports", label: "View Reports" },
      { key: "view_dashboards", label: "View Dashboards" },
      { key: "export_data", label: "Export Data" },
    ],
  },
  {
    category: "Settings",
    icon: Settings,
    permissions: [
      { key: "settings_general", label: "General Settings" },
      { key: "settings_notifications", label: "Notification Settings" },
      { key: "settings_branding", label: "Branding Settings" },
      { key: "settings_payments", label: "Payment Settings" },
      { key: "settings_zoom", label: "Zoom Settings" },
    ],
  },
  {
    category: "System & Administration",
    icon: Monitor,
    permissions: [
      { key: "manage_admins", label: "Manage Admins" },
      { key: "manage_branches", label: "Manage Branches" },
      { key: "manage_assets", label: "Manage Assets" },
      { key: "send_notifications", label: "Send Notifications" },
      { key: "access_audit_logs", label: "Access Audit Logs" },
    ],
  },
  {
    category: "Compliance & Mandatory Training",
    icon: ShieldCheck,
    permissions: [
      { key: "manage_mandatory_training", label: "Manage Mandatory Training Campaigns" },
      { key: "manage_employee_groups", label: "Manage Employee Groups" },
      { key: "view_compliance_reports", label: "View Compliance Reports" },
    ],
  },
];

/**
 * Shown ONLY in a distributor institute's admin editor (hooks/usePermissionCategories),
 * so no other institute's permission counts change. A super admin always has
 * all three; an admin needs them to reach the Distributor section.
 */
export const distributorPermissionCategory = {
  category: "Distributor",
  icon: Handshake,
  permissions: [
    { key: "distributor_view", label: "View Clients" },
    { key: "distributor_manage", label: "Switch Clients On / Off" },
    { key: "distributor_billing", label: "View Client Billing" },
    // Advanced distribution only — usePermissionCategories drops it elsewhere.
    { key: "distributor_institutes", label: "Create & Open Institutes" },
  ],
};

/** The permission that exists only while advanced distribution is on. */
export const ADVANCED_DISTRIBUTOR_PERMISSION = "distributor_institutes";

export const TOTAL_PERMISSIONS = permissionCategories.reduce(
  (sum, c) => sum + c.permissions.length, 0
);

export const ALL_PERMISSION_KEYS = permissionCategories.flatMap(
  (c) => c.permissions.map((p) => p.key)
);

export const ROLE_OPTIONS = ["Super Admin", "Admin", "Moderator"];

export const DEPARTMENT_OPTIONS = ["IT", "Admin", "HR", "Academics", "Finance", "Marketing"];
